import React, { useState, useMemo, useEffect } from 'react'
import { IoIosCloseCircleOutline } from 'react-icons/io'
import { FiAlertCircle } from 'react-icons/fi'
import { FiClock } from 'react-icons/fi'
import Select from 'react-select'
import useSWR from 'swr'
import axios from 'axios'
import { BASE_URL, fetcher, fetchAll } from '../../utils/api'
import Spinner from '../Spinner'
import { toast } from 'react-toastify'
import {
  getOutboundSmsSenderId,
  getSenderIdLabel,
  PLATFORM_DEFAULT_SENDER_ID,
} from '../../utils/smsSenderId'
import {
  DEFAULT_MEASURING_UNIT,
  MEASURING_UNITS,
  buildHarvestRequestPayload,
  formatHarvestRequestError,
  getPriceFieldLabel,
  getWeightFieldLabel,
} from '../../utils/harvestRequest'
import ScheduleRepeatFields from '../scheduling/ScheduleRepeatFields'
import {
  HARVEST_REPEAT_OPTIONS,
  buildScheduleApiFields,
  validateScheduleConfig,
  parseScheduleFromApi,
} from '../../utils/scheduleRepeat'

// const GROUPS = [ ... ] // Remove static
const CURRENT_MARKET_PRICE = 350

const AddHarvestRequestModal = ({
  showAddModal,
  setShowAddModal,
  onClose,
  mutate
}) => {
  const [sendToAll, setSendToAll] = useState(true)
  const [schedule, setSchedule] = useState(false)
  const [repeat, setRepeat] = useState('weekly')
  const [selectedDay, setSelectedDay] = useState('Monday')
  const [selectedDate, setSelectedDate] = useState('')
  const [selectedMonths, setSelectedMonths] = useState([])
  const [selectedDayOfMonth, setSelectedDayOfMonth] = useState('')
  const [selectedTime, setSelectedTime] = useState('00:00')
  const [scheduleErrors, setScheduleErrors] = useState({})
  const [selectedGroups, setSelectedGroups] = useState([])
  const [title, setTitle] = useState('')
  const [weight, setWeight] = useState('')
  const [measuringUnit, setMeasuringUnit] = useState(DEFAULT_MEASURING_UNIT)
  const [selectedCommodity, setSelectedCommodity] = useState(null)
  const [price, setPrice] = useState('')
  const [bunches, setBunches] = useState('')
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(false)
  const [smsSenderId, setSmsSenderId] = useState(PLATFORM_DEFAULT_SENDER_ID)

  // Fetch SMS sender IDs
  const { data: smsSenderIdsData, isLoading: loadingSenderIds } = useSWR(`sms-sender-ids/`, fetcher)

  const smsSenderIds = Array.isArray(smsSenderIdsData) ? smsSenderIdsData : []
  const approvedSenderIds = useMemo(
    () => smsSenderIds.filter((id) => id.status === 'approved'),
    [smsSenderIds]
  )
  const availableSenderIds = useMemo(
    () => approvedSenderIds.filter((id) => id.is_enabled),
    [approvedSenderIds]
  )

  useEffect(() => {
    if (loadingSenderIds) return
    setSmsSenderId(getOutboundSmsSenderId(smsSenderIds))
  }, [loadingSenderIds, smsSenderIds])

  // Fetch groups from API
  const { data: groups, isLoading: groupsLoading, error: groupsError } = useSWR(
    'groups/',
    fetchAll
  )

  const { data: commodities, isLoading: commoditiesLoading } = useSWR(
    'commodities/',
    fetcher
  )

  // Convert groups to react-select format
  const groupOptions = groups ? groups.map(group => ({
    value: group.id,
    label: `${group.group_name} - ${group.total_members} members` || group.name
  })) : []


  const handleCommodityChange = (selectedOption) => {
    setSelectedCommodity(selectedOption)
  }

  // Handle group selection with react-select
  const handleGroupChange = (selectedOptions) => {
    setSelectedGroups(selectedOptions || [])
  }

  // Build payload and submit
  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const token = localStorage.getItem('token')

      if (schedule) {
        const scheduleValidation = validateScheduleConfig({
          repeat,
          date: selectedDate,
          time: selectedTime,
          day: selectedDay,
          months: selectedMonths,
          dayOfMonth: selectedDayOfMonth,
        })
        if (Object.keys(scheduleValidation).length > 0) {
          setScheduleErrors(scheduleValidation)
          toast.error(Object.values(scheduleValidation)[0])
          setLoading(false)
          return
        }
      }
      setScheduleErrors({})
      if (!selectedCommodity?.value) {
        toast.error('Please select a commodity')
        setLoading(false)
        return
      }
      if (!sendToAll && selectedGroups.length === 0) {
        toast.error('Please select at least one group')
        setLoading(false)
        return
      }
      if (!weight || Number.isNaN(parseFloat(weight))) {
        toast.error('Please enter a valid weight')
        setLoading(false)
        return
      }
      if (!price || Number.isNaN(parseFloat(price))) {
        toast.error('Please enter a valid price')
        setLoading(false)
        return
      }

      const resolvedSenderId = getOutboundSmsSenderId(smsSenderIds, smsSenderId)

      const scheduleFields = schedule
        ? buildScheduleApiFields({
            repeat,
            date: selectedDate,
            time: selectedTime,
            day: selectedDay,
            months: selectedMonths,
            dayOfMonth: selectedDayOfMonth,
          })
        : {}

      const payload = buildHarvestRequestPayload({
        weight,
        price,
        measuringUnit,
        title,
        to_all_farmer: sendToAll,
        commodity_id: selectedCommodity.value,
        sms_nickname: resolvedSenderId,
        sms_sender_id: resolvedSenderId,
        target_group_ids: sendToAll ? [] : selectedGroups.map((group) => group.value),
        request_status: schedule ? 'scheduled' : undefined,
        notes,
        ...scheduleFields,
      })
      await axios.post(`${BASE_URL}harvest-requests/`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      mutate()
      toast.success('Request created!')
      setShowAddModal(false)
    } catch (err) {
      toast.error(formatHarvestRequestError(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[99999] backdrop-blur-md">
      <div className="bg-white dark:bg-dark rounded-lg shadow-lg w-full md:max-w-xl relative max-h-[90vh] flex flex-col overflow-hidden">
        <div className='flex flex-row justify-between items-center border-b border-gray-200 p-4 flex-shrink-0 dark:border-border-dark'>
          <h2 className='text-xl font-bold dark:text-white'>
            Create New Request
          </h2>
          <button
            onClick={() => setShowAddModal(false)}
            className="absolute top-4 right-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <IoIosCloseCircleOutline className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-6">
          <h2 className="text-base font-semibold text-yellowcolor mb-4">Request Details</h2>

          {/* Send to All Farmers */}
          <button
            type="button"
            onClick={() => setSendToAll(!sendToAll)}
            className="flex items-start gap-2">
            <div className="mt-1">
              <span className={`inline-flex items-center justify-center w-5 h-5 rounded border-2 ${sendToAll ? 'border-yellowcolor bg-yellow-100' : 'border-gray-300 bg-white'}`}>
                {sendToAll && <svg className="w-4 h-4 text-yellowcolor" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
              </span>
            </div>
            <div>
              <div className="font-semibold text-black text-sm flex items-center gap-1 dark:text-white">
                <span>Send To All Farmers</span>
              </div>
              <div className="text-xs text-gray-400 text-start">This allows you to send the request to all farmers</div>
            </div>
          </button>

          {/* Title Field */}
          <div>
            <label className="block text-sm font-medium mb-1 dark:text-white">Request Title<span className="text-red-500">*</span></label>
            <input
              type="text"
              className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
              placeholder="Enter request title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
            />
          </div>

          {/* Group Selection */}
          {!sendToAll && (
            <div>
              <label className="block text-sm font-medium mb-1 dark:text-white">Select Groups<span className="text-red-500">*</span></label>
              {groupsLoading ? <Spinner /> : groupsError ? <div className="text-red-500 text-sm">Failed to load groups</div> : (
                <Select
                  isMulti
                  options={groupOptions}
                  value={selectedGroups}
                  onChange={handleGroupChange}
                  placeholder="Select groups..."
                  className="text-sm"
                  classNamePrefix="select"
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      borderColor: state.isFocused ? '#fbbf24' : '#e5e7eb',
                      boxShadow: state.isFocused ? '0 0 0 1px #fbbf24' : 'none',
                      '&:hover': {
                        borderColor: '#fbbf24'
                      }
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isSelected ? '#fbbf24' : state.isFocused ? '#fef3c7' : 'white',
                      color: state.isSelected ? 'white' : 'black',
                      '&:hover': {
                        backgroundColor: state.isSelected ? '#fbbf24' : '#fef3c7'
                      }
                    }),
                    multiValue: (provided) => ({
                      ...provided,
                      backgroundColor: '#fbbf24',
                      color: 'white'
                    }),
                    multiValueLabel: (provided) => ({
                      ...provided,
                      color: 'white'
                    }),
                    multiValueRemove: (provided) => ({
                      ...provided,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#d97706',
                        color: 'white'
                      }
                    })
                  }}
                />
              )}
            </div>
          )}

          {/* Commodity Selection */}
          <div>
            <label className="block text-sm font-medium mb-1 dark:text-white">Select Commodity<span className="text-red-500">*</span></label>
            <Select
              options={commodities?.map(commodity => ({
                value: commodity.id,
                label: commodity.name
              }))}
              value={selectedCommodity}
              onChange={handleCommodityChange}
              placeholder="Select commodity..."
            />
          </div>

          {/* Measuring Unit */}
          <div>
            <label className="block text-sm font-medium mb-1 dark:text-white">Measuring Unit<span className="text-red-500">*</span></label>
            <select
              className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent"
              value={measuringUnit}
              onChange={(e) => setMeasuringUnit(e.target.value)}
              required
            >
              {MEASURING_UNITS.map((unit) => (
                <option key={unit.value} value={unit.value}>{unit.label}</option>
              ))}
            </select>
          </div>

          {/* Weight */}
          <div>
            <label className="block text-sm font-medium mb-1 dark:text-white">{getWeightFieldLabel(measuringUnit)}<span className="text-red-500">*</span></label>
            <input
              type="number"
              className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
              placeholder={`Enter weight in ${measuringUnit}`}
              value={weight}
              onChange={e => setWeight(e.target.value)}
              required
            />
          </div>

          {/* Price Offer */}
          <div>
            <label className="block text-sm font-medium mb-1 dark:text-white">{getPriceFieldLabel(measuringUnit)}<span className="text-red-500">*</span></label>
            <input
              type="number"
              className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
              placeholder="eg. 300"
              value={price}
              onChange={e => setPrice(e.target.value)}
              required
            />
            {/* <div className="flex items-center gap-1 text-yellowcolor text-xs mt-1">
              <FiAlertCircle className="inline-block" />
              <span>Current Market Price (GHS {CURRENT_MARKET_PRICE})</span>
            </div> */}
          </div>

          {/* Number of Bunches */}
          {/* <div>
            <label className="block text-sm font-medium mb-1 dark:text-white">Est. Number Of Bunches:<span className="text-red-500">*</span></label>
            <input
              type="number"
              className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
              placeholder="Enter number of bunches"
              value={bunches}
              onChange={e => setBunches(e.target.value)}
              required
            />
          </div> */}

          {/* SMS Sender ID */}
          <div>
            <label className="block text-sm font-medium mb-1 dark:text-white">SMS Nickname</label>
            {loadingSenderIds ? (
              <Spinner />
            ) : availableSenderIds.length > 0 ? (
              <select
                className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent"
                value={smsSenderId}
                onChange={(e) => setSmsSenderId(e.target.value)}
              >
                {availableSenderIds.map((sender) => {
                  const label = getSenderIdLabel(sender)
                  return (
                    <option key={sender.id} value={label}>
                      {label}
                    </option>
                  )
                })}
              </select>
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Using default:{' '}
                <span className="font-semibold ">{smsSenderId || PLATFORM_DEFAULT_SENDER_ID}</span>
              </p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium mb-1 dark:text-white">Notes</label>
            <textarea
              className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
              placeholder="Add any notes (optional)"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
            />
          </div>

          {/* Schedule Toggle */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSchedule(!schedule)}
              className={`w-10 h-6 flex items-center rounded-full p-1 transition-colors duration-200 ${schedule ? 'bg-yellowcolor' : 'bg-gray-200'}`}
            >
              <span className={`h-4 w-4 bg-white rounded-full shadow transform transition-transform duration-200 ${schedule ? 'translate-x-4' : ''}`}></span>
            </button>
            <span className="font-medium text-gray-700 dark:text-white">Schedule This Request</span>
          </div>

          {/* Scheduling Section */}
          {schedule && (
            <ScheduleRepeatFields
              repeat={repeat}
              onRepeatChange={setRepeat}
              repeatOptions={HARVEST_REPEAT_OPTIONS}
              date={selectedDate}
              onDateChange={setSelectedDate}
              day={selectedDay}
              onDayChange={setSelectedDay}
              months={selectedMonths}
              onMonthsChange={setSelectedMonths}
              dayOfMonth={selectedDayOfMonth}
              onDayOfMonthChange={setSelectedDayOfMonth}
              time={selectedTime}
              onTimeChange={setSelectedTime}
              errors={scheduleErrors}
            />
          )}

          <div className='border-t pt-4 z-50 border-gray-200 flex flex-row items-end justify-end w-full bg-white dark:bg-dark flex-shrink-0'>
            <div className="flex flex-row items-end justify-end gap-4">
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className='px-6 text-sm py-2 bg-white dark:bg-dark border dark:border-border-dark dark:text-white text-black rounded-lg'>
                Cancel
              </button>

              <button
                type="submit"
                disabled={loading}
                className={`px-6 text-sm py-2 bg-yellowcolor text-black rounded-lg flex items-center justify-center`}>
                {loading ? "Please wait..." : 'Send Request'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddHarvestRequestModal